import React, { useState } from "react";
import { Users, CheckCircle, XCircle, Clock, Search, Eye, FileText, Download, Shield, ChevronDown, ChevronUp, Phone, Mail, MapPin, Briefcase, Building2, CreditCard, X, AlertTriangle, UserPlus, Copy, Link2, MessageCircle, Pencil, Trash2, Upload, Save, Loader2 } from "lucide-react";
import { logger } from "../utils/logger";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { adminRefreshBus } from "../utils/adminRefreshBus";
import RefreshControl from "./admin/RefreshControl";
import MapNavButton from "./MapNavButton";
import VendorEditModal from "./VendorEditModal";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  suspended: "bg-gray-100 text-gray-700 border-gray-200",
};

const STATUS_ICONS = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  suspended: AlertTriangle,
};

const DOC_LABELS = {
  pan: "PAN Card",
  aadhar: "Aadhar Card",
  gst_certificate: "GST Certificate",
  cancel_check: "Cancelled Cheque",
  other: "Other Document",
};

export const VendorTab = ({ auth, authAxios, searchQuery }) => {
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [showMagicModal, setShowMagicModal] = useState(false);

  // iter163 — universal auto-refresh. Fetches on mount, on focus/visibility
  // change, on bus signal from `vendors` scope, and polls every 60s.
  const { data: vendorData, loading, refresh, lastUpdated } = useAutoRefresh(
    async () => {
      const res = await authAxios.get("/api/vendors/admin/list");
      return res.data;
    },
    { scope: "vendors", interval: 60_000 }
  );
  const vendors = vendorData?.vendors || [];
  const stats = vendorData?.stats || {};

  const updateVendorStatus = async (vendorId, newStatus) => {
    try {
      await authAxios.put(`/api/vendors/admin/${vendorId}`, { status: newStatus });
      adminRefreshBus.emit("vendors");
    } catch (err) {
      logger.error("Failed to update vendor:", err);
    }
  };

  const verifyDocument = async (vendorId, docId) => {
    try {
      await authAxios.post(`/api/vendors/admin/${vendorId}/verify-document/${docId}`);
      adminRefreshBus.emit("vendors");
    } catch (err) {
      logger.error("Failed to verify document:", err);
    }
  };

  const downloadDocument = async (vendorId, docId, filename) => {
    try {
      const res = await authAxios.get(`/api/vendors/admin/${vendorId}/document/${docId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'document';
      link.click();
    } catch (err) {
      logger.error("Failed to download:", err);
    }
  };

  // Super-Admin-only (role === "admin") vendor edit. Backend also enforces this.
  const isSuperAdmin = auth?.role === "admin";
  const [editVendor, setEditVendor] = useState(null);

  const filteredVendors = vendors.filter(v => {
    if (statusFilter !== "all" && v.status !== statusFilter) return false;
    if (serviceFilter !== "all" && v.service_type !== serviceFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (v.name || "").toLowerCase().includes(q)
      || (v.phone || "").includes(q)
      || (v.email || "").toLowerCase().includes(q)
      || (v.service_type || "").toLowerCase().includes(q)
      || (v.id || "").toLowerCase().includes(q);
  });

  const serviceTypes = [...new Set(vendors.map(v => v.service_type).filter(Boolean))];

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-[#E83E8C] border-t-transparent rounded-full"></div></div>;

  return (
    <div data-testid="vendor-tab">
      {/* iter163 — refresh chip; shows "Updated Xs ago" + manual refresh */}
      <div className="flex items-center justify-end mb-3">
        <RefreshControl
          lastUpdated={lastUpdated}
          onRefresh={() => refresh({ background: false })}
          loading={loading}
        />
      </div>

      {/* Stats Cards — clickable chip-filters. Clicking a stat scopes the
          table to that status; clicking the already-active one (or "Total")
          clears the filter. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: "Total",     value: stats.total || 0,     filter: "all",       color: "bg-[#1E1B4B]",  textColor: "text-white",         ring: "ring-white" },
          { label: "Pending",   value: stats.pending || 0,   filter: "pending",   color: "bg-amber-50",   textColor: "text-amber-700",     ring: "ring-amber-500" },
          { label: "Approved",  value: stats.approved || 0,  filter: "approved",  color: "bg-green-50",   textColor: "text-green-700",     ring: "ring-green-500" },
          { label: "Rejected",  value: stats.rejected || 0,  filter: "rejected",  color: "bg-red-50",     textColor: "text-red-700",       ring: "ring-red-500" },
          { label: "Suspended", value: stats.suspended || 0, filter: "suspended", color: "bg-gray-100",   textColor: "text-gray-700",      ring: "ring-gray-500" },
        ].map(s => {
          const active = statusFilter === s.filter;
          return (
            <button
              type="button"
              key={s.label}
              onClick={() => setStatusFilter(active && s.filter !== "all" ? "all" : s.filter)}
              className={`${s.color} rounded-xl p-4 text-center transition-all cursor-pointer hover:scale-[1.02] hover:shadow-md focus:outline-none ${active ? `ring-2 ring-offset-2 ${s.ring} shadow-lg` : "ring-0"}`}
              data-testid={`vendor-stat-${s.label.toLowerCase()}`}
              aria-pressed={active}
              aria-label={`${s.label}: ${s.value}${active ? " — active filter" : " — click to filter"}`}
            >
              <p className={`text-2xl font-bold ${s.textColor}`}>{s.value}</p>
              <p className={`text-xs ${s.textColor} opacity-75`}>{s.label}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm text-[#475569] focus:border-[#E83E8C] outline-none"
          data-testid="vendor-status-filter">
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
        </select>
        <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm text-[#475569] focus:border-[#E83E8C] outline-none"
          data-testid="vendor-service-filter">
          <option value="all">All Services</option>
          {serviceTypes.map(st => <option key={st} value={st}>{st}</option>)}
        </select>
        <button
          onClick={() => setShowMagicModal(true)}
          className="ml-auto px-4 py-2 bg-gradient-to-r from-[#E83E8C] to-purple-600 text-white text-sm font-semibold rounded-xl hover:opacity-90 flex items-center gap-1.5"
          data-testid="vendor-add-btn"
        >
          <UserPlus size={14} /> Add Vendor (magic link)
        </button>
      </div>

      {showMagicModal && (
        <VendorMagicLinkModal authAxios={authAxios} onClose={() => setShowMagicModal(false)} />
      )}
      {editVendor && isSuperAdmin && (
        <VendorEditModal
          vendor={editVendor}
          authAxios={authAxios}
          docLabels={DOC_LABELS}
          onClose={() => { setEditVendor(null); adminRefreshBus.emit("vendors"); }}
          onSaved={() => { setEditVendor(null); adminRefreshBus.emit("vendors"); }}
        />
      )}

      {/* Vendor List */}
      {filteredVendors.length === 0 ? (
        <div className="text-center py-16 text-[#94a3b8]">
          <Users size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium">No vendors found</p>
          <p className="text-sm">Share the vendor registration link to onboard partners</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredVendors.map(vendor => {
            const isExpanded = expandedVendor === vendor.id;
            const StatusIcon = STATUS_ICONS[vendor.status] || Clock;
            const docsCount = vendor.documents?.length || 0;
            const verifiedDocs = vendor.documents?.filter(d => d.verified)?.length || 0;

            return (
              <div key={vendor.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`vendor-card-${vendor.id}`}>
                {/* Header */}
                <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedVendor(isExpanded ? null : vendor.id)}>
                  <div className="w-10 h-10 rounded-xl bg-[#1E1B4B] flex items-center justify-center text-white font-bold text-sm">
                    {vendor.name?.charAt(0)?.toUpperCase() || "V"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[#1E1B4B] truncate">{vendor.name}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${STATUS_COLORS[vendor.status] || STATUS_COLORS.pending}`}>
                        {vendor.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#94a3b8] mt-1">
                      <span className="flex items-center gap-1"><Briefcase size={11} />{vendor.service_type}</span>
                      <span className="flex items-center gap-1"><Phone size={11} />{vendor.phone}</span>
                      <span className="flex items-center gap-1"><FileText size={11} />{verifiedDocs}/{docsCount} docs</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {vendor.status === "pending" && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); updateVendorStatus(vendor.id, "approved"); }}
                          className="p-2 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-colors"
                          data-testid={`approve-vendor-${vendor.id}`} title="Approve">
                          <CheckCircle size={16} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); updateVendorStatus(vendor.id, "rejected"); }}
                          className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
                          data-testid={`reject-vendor-${vendor.id}`} title="Reject">
                          <XCircle size={16} />
                        </button>
                      </>
                    )}
                    {isExpanded ? <ChevronUp size={16} className="text-[#94a3b8]" /> : <ChevronDown size={16} className="text-[#94a3b8]" />}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4 bg-[#FFFAF5]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Contact & Service */}
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-[#1E1B4B] uppercase tracking-wider">Contact & Service</h4>
                        <div className="space-y-1 text-sm text-[#475569]">
                          <p className="flex items-center gap-2"><Phone size={13} /> {vendor.phone}</p>
                          {vendor.email && <p className="flex items-center gap-2"><Mail size={13} /> {vendor.email}</p>}
                          <p className="flex items-center gap-2"><Briefcase size={13} /> {vendor.service_type}</p>
                          <p className="flex items-center gap-2"><MapPin size={13} /> {(vendor.service_area || []).join(", ") || "Not specified"}</p>
                          {(() => {
                            const loc = vendor.shop_location || {};
                            return (loc.lat && loc.lng) ? (
                              <MapNavButton lat={loc.lat} lng={loc.lng} label="Navigate to shop" testId={`vendor-navigate-${vendor.id}`} className="mt-1" />
                            ) : null;
                          })()}
                          {vendor.experience_years > 0 && <p>Experience: {vendor.experience_years} years</p>}
                          {vendor.pricing_range && <p>Pricing: {vendor.pricing_range}</p>}
                        </div>
                      </div>

                      {/* Bank Details */}
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-[#1E1B4B] uppercase tracking-wider">Bank & Payment</h4>
                        <div className="space-y-1 text-sm text-[#475569]">
                          {vendor.bank_name && <p className="flex items-center gap-2"><Building2 size={13} /> {vendor.bank_name}</p>}
                          {vendor.bank_account_holder && <p>A/C Holder: {vendor.bank_account_holder}</p>}
                          {vendor.bank_account_number && <p>A/C No: ****{vendor.bank_account_number.slice(-4)}</p>}
                          {vendor.bank_ifsc && <p>IFSC: {vendor.bank_ifsc}</p>}
                          {vendor.upi_id && <p className="flex items-center gap-2"><CreditCard size={13} /> UPI: {vendor.upi_id}</p>}
                          {vendor.gst_number && <p>GST: {vendor.gst_number}</p>}
                          <p>Payment Cycle: {vendor.payment_cycle || "Monthly"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    {vendor.documents && vendor.documents.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-bold text-[#1E1B4B] uppercase tracking-wider mb-2">Documents</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {vendor.documents.map(doc => (
                            <div key={doc.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100">
                              <FileText size={16} className={doc.verified ? "text-green-500" : "text-amber-500"} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#1E1B4B] truncate">{DOC_LABELS[doc.type] || doc.type}</p>
                                <p className="text-xs text-[#94a3b8]">{doc.filename}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                {!doc.verified && (
                                  <button onClick={() => verifyDocument(vendor.id, doc.id)}
                                    className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors" title="Verify">
                                    <Shield size={13} />
                                  </button>
                                )}
                                {doc.verified && <CheckCircle size={15} className="text-green-500" />}
                                <button onClick={() => downloadDocument(vendor.id, doc.id, doc.filename)}
                                  className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" title="Download">
                                  <Download size={13} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Admin Notes */}
                    {vendor.about && (
                      <div className="mb-4">
                        <h4 className="text-xs font-bold text-[#1E1B4B] uppercase tracking-wider mb-1">About</h4>
                        <p className="text-sm text-[#475569]">{vendor.about}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                      {vendor.status !== "approved" && (
                        <button onClick={() => updateVendorStatus(vendor.id, "approved")}
                          className="px-4 py-2 bg-green-500 text-white text-sm rounded-xl hover:bg-green-600 transition-colors">
                          Approve
                        </button>
                      )}
                      {vendor.status !== "suspended" && vendor.status !== "rejected" && (
                        <button onClick={() => updateVendorStatus(vendor.id, "suspended")}
                          className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-xl hover:bg-gray-300 transition-colors">
                          Suspend
                        </button>
                      )}
                      {vendor.status === "suspended" && (
                        <button onClick={() => updateVendorStatus(vendor.id, "approved")}
                          className="px-4 py-2 bg-blue-500 text-white text-sm rounded-xl hover:bg-blue-600 transition-colors">
                          Reactivate
                        </button>
                      )}
                      {isSuperAdmin && (
                        <button onClick={() => setEditVendor(vendor)}
                          className="px-4 py-2 bg-[#1E1B4B] text-white text-sm rounded-xl hover:opacity-90 transition-colors flex items-center gap-1.5"
                          data-testid={`edit-vendor-${vendor.id}`} title="Edit vendor (Super Admin)">
                          <Pencil size={14} /> Edit Details
                        </button>
                      )}
                      <span className="text-xs text-[#94a3b8] self-center ml-auto">ID: {vendor.id} | Applied: {new Date(vendor.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Magic-link onboarding modal (iter148)
// ──────────────────────────────────────────────────────────────────────
const VendorMagicLinkModal = ({ authAxios, onClose }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [serviceType, setServiceType] = useState("decorator");
  const [expiresInHours, setExpiresInHours] = useState(72);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!name.trim() || !phone.trim() || !serviceType.trim()) {
      setError("Name, phone, and service type are required");
      return;
    }
    setBusy(true); setError("");
    try {
      const r = await authAxios.post("/api/vendors/admin/magic-link", {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        service_type: serviceType,
        expires_in_hours: Number(expiresInHours),
      });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create magic link");
    }
    setBusy(false);
  };

  const copyText = (text) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const waUrl = result
    ? `https://wa.me/${(phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(result.whatsapp_template)}`
    : "";

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="vendor-magic-modal"
    >
      <div className="bg-white rounded-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2">
              <UserPlus size={18} className="text-[#E83E8C]" /> Add Vendor
            </h3>
            <p className="text-xs text-[#64748B] mt-1">
              Generates a one-time onboarding link. Vendor opens it → form auto-filled with what you typed.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="vendor-magic-close">
            <X size={18} />
          </button>
        </div>

        {!result ? (
          <>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1">Name *</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Rahul Sharma"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#E83E8C] outline-none"
                  data-testid="vendor-magic-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1">Phone *</label>
                <input
                  type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="9876543210"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#E83E8C] outline-none"
                  data-testid="vendor-magic-phone"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1">Email (optional)</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="vendor@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#E83E8C] outline-none"
                  data-testid="vendor-magic-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] mb-1">Service type *</label>
                  <select value={serviceType} onChange={(e) => setServiceType(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#E83E8C] outline-none bg-white"
                    data-testid="vendor-magic-service">
                    <option value="decorator">Decorator</option>
                    <option value="cake">Cake</option>
                    <option value="balloons">Balloons</option>
                    <option value="photographer">Photographer</option>
                    <option value="catering">Catering</option>
                    <option value="dj">DJ / Music</option>
                    <option value="entertainer">Entertainer</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] mb-1">Link expires in (hrs)</label>
                  <input
                    type="number" min={1} max={720} value={expiresInHours}
                    onChange={(e) => setExpiresInHours(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#E83E8C] outline-none"
                    data-testid="vendor-magic-expires"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="vendor-magic-error">
                {error}
              </div>
            )}

            <button
              onClick={submit}
              disabled={busy}
              className="mt-4 w-full px-4 py-2.5 bg-gradient-to-r from-[#E83E8C] to-purple-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center justify-center gap-1.5"
              data-testid="vendor-magic-submit"
            >
              <Link2 size={14} /> {busy ? "Creating link..." : "Generate magic link"}
            </button>
          </>
        ) : (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
              <div className="text-xs font-semibold text-emerald-800 mb-1">✅ Magic link created — share with vendor</div>
              <div className="text-[10px] text-emerald-700">Expires {new Date(result.expires_at).toLocaleString("en-IN")} · single-use</div>
            </div>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1">Onboarding URL</label>
                <div className="flex gap-1.5">
                  <input readOnly value={result.url}
                    className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 font-mono"
                    data-testid="vendor-magic-result-url" />
                  <button onClick={() => copyText(result.url)}
                    className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-50 flex items-center gap-1"
                    data-testid="vendor-magic-copy-url">
                    <Copy size={12} /> {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] mb-1">WhatsApp message</label>
                <textarea readOnly value={result.whatsapp_template} rows={5}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 font-mono"
                  data-testid="vendor-magic-result-wa" />
                <div className="flex gap-2 mt-2">
                  <button onClick={() => copyText(result.whatsapp_template)}
                    className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-50 flex items-center justify-center gap-1"
                    data-testid="vendor-magic-copy-wa">
                    <Copy size={12} /> Copy WhatsApp text
                  </button>
                  <a href={waUrl} target="_blank" rel="noreferrer"
                    className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700 flex items-center justify-center gap-1"
                    data-testid="vendor-magic-open-wa">
                    <MessageCircle size={12} /> Open WhatsApp
                  </a>
                </div>
              </div>
            </div>
            <button onClick={onClose} data-testid="vendor-magic-done"
              className="mt-4 w-full px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200">
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
};
